import { useLayoutEffect, useState } from 'react';

/**
 * Returns the DOM node into which Fluent v9 portal-based components
 * (Menu, Popover, Tooltip, TagPicker, …) should mount their popovers.
 *
 * Fallback order: `#portal-root` → `#root` → `document.body`.
 *
 * `#portal-root` is rendered inside the app's `FluentProvider` in
 * `Layout.tsx`, so mounting portals there guarantees they inherit the
 * custom theme tokens. Components that leave Fluent's default mount
 * (`document.body`) get wrapped in a fresh FluentProvider by Fluent
 * itself — that works for theme inheritance via React context, but it
 * loses inheritance of any global CSS / styling written against the
 * app-level provider.
 *
 * Returns `null` on the first render (SSR-safe, no DOM access during
 * render). The calling component should pass the value through unchanged
 * to Fluent's `mountNode` prop; Fluent falls back to its own default
 * until the effect runs on the client.
 */
export const useFluentMountNode = (): HTMLElement | null => {
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useLayoutEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    setMountNode(
      document.querySelector<HTMLElement>('#portal-root') ||
        document.querySelector<HTMLElement>('#root') ||
        document.body,
    );
  }, []);

  return mountNode;
};
